/**
 * Service worker registration
 *
 * This module:
 * - Registers the service worker
 * - Handles service worker updates
 * - Provides methods to communicate with the service worker
 */
import { DEFAULT_SW_SCOPE } from "../config";
import { ServiceWorkerMessageType } from "../config/types";
import { debugLog } from "../utils/cache-helpers";

/**
 * Options for service worker registration
 */
interface RegisterOptions {
  scope?: string;
  updateViaCache?: ServiceWorkerUpdateViaCache;
  immediate?: boolean;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
}

/**
 * Register the service worker
 * @param swPath Path to the service worker file
 * @param options Registration options
 * @returns Promise resolving to the service worker registration
 */
export async function registerServiceWorker(
  swPath: string = "/service-worker.js",
  options: RegisterOptions = {}
): Promise<ServiceWorkerRegistration | null> {
  // Check if service workers are supported
  if (!("serviceWorker" in navigator)) {
    debugLog("Service workers are not supported in this browser");
    return null;
  }

  try {
    // Register the service worker
    const registration = await navigator.serviceWorker.register(swPath, {
      scope: options.scope || DEFAULT_SW_SCOPE,
      updateViaCache: options.updateViaCache || "none",
    });

    debugLog("Service worker registered:", registration);

    // Call success callback if provided
    if (options.onSuccess) {
      options.onSuccess();
    }

    // Handle updates
    if (options.immediate) {
      await forceUpdate(registration);
    }

    // Set up update detection
    setupUpdateDetection(registration, options.onUpdate);

    return registration;
  } catch (error) {
    debugLog("Service worker registration failed:", error);

    // Call error callback if provided
    if (options.onError && error instanceof Error) {
      options.onError(error);
    }

    return null;
  }
}

/**
 * Force update of the service worker
 * @param registration Service worker registration
 */
export async function forceUpdate(
  registration: ServiceWorkerRegistration
): Promise<void> {
  try {
    await registration.update();
    debugLog("Service worker updated");

    if (registration.waiting) {
      // If there's a waiting service worker, activate it
      debugLog("New service worker waiting, activating...");
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
  } catch (error) {
    debugLog("Service worker update failed:", error);
  }
}

/**
 * Set up detection for service worker updates
 * @param registration Service worker registration
 * @param callback Callback to call when an update is available
 */
function setupUpdateDetection(
  registration: ServiceWorkerRegistration,
  callback?: (registration: ServiceWorkerRegistration) => void
): void {
  // Check for updates on page load
  if (registration.waiting) {
    debugLog("New service worker waiting");
    if (callback) {
      callback(registration);
    }
    return;
  }

  // Listen for new installing workers
  const handleUpdate = () => {
    if (registration.installing) {
      // Track progress
      registration.installing.addEventListener("statechange", () => {
        if (registration.waiting) {
          debugLog("New service worker installed and waiting");
          if (callback) {
            callback(registration);
          }
        }
      });
    }
  };

  // Initial check
  handleUpdate();

  // Listen for future updates
  registration.addEventListener("updatefound", handleUpdate);
}

/**
 * Send a message to the service worker
 * @param type Message type
 * @param payload Message payload
 * @returns Promise resolving to the response
 */
export async function sendMessageToSW<T = any>(
  type: ServiceWorkerMessageType,
  payload?: any
): Promise<T | null> {
  if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) {
    debugLog("No active service worker found");
    return null;
  }

  try {
    // Create a message channel for the response
    const messageChannel = new MessageChannel();

    // Create a promise that will resolve with the response
    const responsePromise = new Promise<T>((resolve) => {
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };
    });

    // Send the message
    navigator.serviceWorker.controller.postMessage({ type, payload }, [
      messageChannel.port2,
    ]);

    // Wait for the response with a timeout
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), 3000);
    });

    return Promise.race([responsePromise, timeoutPromise]);
  } catch (error) {
    debugLog("Error sending message to service worker:", error);
    return null;
  }
}

/**
 * Check if the service worker is active
 * @returns Whether the service worker is active
 */
export function isServiceWorkerActive(): boolean {
  return !!("serviceWorker" in navigator && navigator.serviceWorker.controller);
}

/**
 * Unregister all service workers
 * @returns Promise resolving when all service workers are unregistered
 */
export async function unregisterAllServiceWorkers(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    await Promise.all(
      registrations.map(async (registration) => {
        const result = await registration.unregister();
        debugLog(`Service worker unregistered:`, result ? "success" : "failed");
      })
    );
  } catch (error) {
    debugLog("Error unregistering service workers:", error);
  }
}
